import { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/api";
import { Video, Volume2, VolumeX } from "lucide-react";

function supportedMime(m) {
  if (!window.MediaSource) return null;
  const list = [];
  if (m) list.push(m);
  if (m && m.startsWith("video/mp4")) list.push('video/mp4;codecs="avc1.42E01E,mp4a.40.2"', 'video/mp4;codecs="avc1.64001f,mp4a.40.2"');
  if (m && m.startsWith("video/webm")) list.push("video/webm;codecs=vp8,opus");
  for (const x of list) { try { if (MediaSource.isTypeSupported(x)) return x; } catch (_) {} }
  return null;
}

// View-only live player. Connects as a viewer using the given session token + auth JWT.
export function LiveWatch({ token, authToken }) {
  const videoRef = useRef(null);
  const imgRef = useRef(null);
  const wsRef = useRef(null);
  const msRef = useRef(null);
  const sbRef = useRef(null);
  const queueRef = useRef([]);
  const mimeRef = useRef(null);
  const modeRef = useRef("media");
  const lastUrlRef = useRef(null);
  const [online, setOnline] = useState(false);
  const [mode, setMode] = useState("media");
  const [muted, setMuted] = useState(false);

  const teardown = () => {
    try { wsRef.current && wsRef.current.close(); } catch (_) {}
    try { if (msRef.current && msRef.current.readyState === "open") msRef.current.endOfStream(); } catch (_) {}
    try { lastUrlRef.current && URL.revokeObjectURL(lastUrlRef.current); } catch (_) {}
    wsRef.current = null; msRef.current = null; sbRef.current = null; queueRef.current = []; mimeRef.current = null; lastUrlRef.current = null;
  };

  const flush = () => {
    const sb = sbRef.current;
    if (!sb || sb.updating || queueRef.current.length === 0) return;
    const chunk = queueRef.current.shift();
    try { sb.appendBuffer(chunk); }
    catch (e) {
      if (e && e.name === "QuotaExceededError") {
        try { const b = sb.buffered; if (b.length) sb.remove(b.start(0), Math.max(b.start(0), b.end(b.length - 1) - 8)); } catch (_) {}
        queueRef.current.unshift(chunk);
      }
    }
  };
  const onUpdateEnd = () => {
    const sb = sbRef.current;
    try {
      const b = sb.buffered;
      if (b.length && b.end(b.length - 1) - b.start(0) > 30 && !sb.updating) { sb.remove(b.start(0), b.end(b.length - 1) - 20); return; }
    } catch (_) {}
    flush();
  };
  const ensureSb = () => {
    const ms = msRef.current;
    if (!ms || ms.readyState !== "open" || sbRef.current || !mimeRef.current) return;
    const mime = supportedMime(mimeRef.current);
    if (!mime) return;
    try {
      const sb = ms.addSourceBuffer(mime);
      sb.mode = "sequence";
      sb.addEventListener("updateend", onUpdateEnd);
      sbRef.current = sb;
      flush();
    } catch (_) {}
  };
  const renderJpeg = (data) => {
    const img = imgRef.current; if (!img) return;
    const url = URL.createObjectURL(new Blob([data], { type: "image/jpeg" }));
    img.src = url;
    if (lastUrlRef.current) { try { URL.revokeObjectURL(lastUrlRef.current); } catch (_) {} }
    lastUrlRef.current = url;
  };

  useEffect(() => {
    if (!token) return;
    teardown();
    modeRef.current = "media"; setMode("media");
    const video = videoRef.current;
    const ms = new MediaSource();
    msRef.current = ms;
    video.src = URL.createObjectURL(ms);
    ms.addEventListener("sourceopen", ensureSb);
    const wsBase = BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/live/${token}?role=viewer&auth=${authToken}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onmessage = (e) => {
      if (typeof e.data === "string") {
        try {
          const obj = JSON.parse(e.data);
          if (obj.type === "broadcaster") setOnline(!!obj.online);
          if (obj.type === "mode") { modeRef.current = obj.mode; setMode(obj.mode); }
          if (obj.type === "mime") { mimeRef.current = obj.mime; ensureSb(); }
        } catch (_) {}
        return;
      }
      if (modeRef.current === "jpeg") { setOnline(true); renderJpeg(e.data); return; }
      queueRef.current.push(new Uint8Array(e.data));
      ensureSb(); flush();
      if (video && video.paused) video.play().catch(() => {});
    };
    return () => teardown();
  }, [token, authToken]); // eslint-disable-line

  const toggleMute = () => { const v = videoRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-900" data-testid="live-watch">
      <video ref={videoRef} data-testid="live-watch-video" playsInline autoPlay className="h-full w-full object-contain" style={{ display: mode === "jpeg" ? "none" : "block" }} />
      <img ref={imgRef} alt="" data-testid="live-watch-img" className="h-full w-full object-contain" style={{ display: mode === "jpeg" ? "block" : "none" }} />
      <div className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-bold" style={{ background: online ? "#dc2626" : "rgba(15,23,42,.7)", color: "#fff" }}>
        {online ? <><span className="h-2 w-2 animate-pulse rounded-full bg-white" /> LIVE</> : <><Video className="h-3 w-3" /> Wacht op camera…</>}
      </div>
      {mode !== "jpeg" && (
        <button data-testid="live-watch-mute" onClick={toggleMute} className="absolute right-2 top-2 rounded-lg bg-slate-900/70 p-2 text-white hover:bg-slate-900">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
